import React, { useEffect, useState, useCallback } from "react";
import { RefreshCw, ChevronDown, Upload, X } from "lucide-react";
import { supabase, uploadImage } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { OrderStatusBadge, Spinner, EmptyState } from "../../components/common/UI";
import toast from "react-hot-toast";

// ── RestaurantOrders ──────────────────────────────────────────────────────────
export function RestaurantOrders() {
  const { user } = useAuth();
  const [restaurantId, setRestaurantId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [orderItems, setOrderItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    supabase
      .from("restaurants")
      .select("id")
      .eq("owner_id", user.id)
      .single()
      .then(({ data }) => {
        if (data) setRestaurantId(data.id);
      });
  }, [user]);

  const fetchOrders = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    let query = supabase
      .from("orders")
      .select("*")
      .eq("restaurant_id", restaurantId)
      .order("created_at", { ascending: false });
    if (statusFilter !== "all") query = query.eq("status", statusFilter);
    const { data } = await query;
    setOrders(data || []);
    setLoading(false);
  }, [restaurantId, statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const fetchItems = async (orderId) => {
    if (orderItems[orderId]) {
      setExpandedOrder(expandedOrder === orderId ? null : orderId);
      return;
    }
    const { data } = await supabase
      .from("order_items")
      .select("*")
      .eq("order_id", orderId);
    setOrderItems((p) => ({ ...p, [orderId]: data || [] }));
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const updateStatus = async (orderId, status) => {
    const { error } = await supabase
      .from("orders")
      .update({ status })
      .eq("id", orderId);
    if (error) { toast.error("Failed to update"); return; }
    setOrders((p) => p.map((o) => o.id === orderId ? { ...o, status } : o));
    toast.success(`Order marked as ${status}`);
  };

  return (
    <div className="space-y-6 page-enter">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Orders</h1>
          <p className="text-gray-500 text-sm">{orders.length} order(s)</p>
        </div>
        <button
          onClick={fetchOrders}
          className="flex items-center gap-2 btn-ghost border border-gray-200"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {["all", "pending", "confirmed", "preparing", "ready", "delivered", "cancelled"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium capitalize border transition-colors ${
              statusFilter === s
                ? "bg-blue-600 text-white border-blue-600"
                : "border-gray-200 text-gray-600"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : orders.length === 0 ? (
        <EmptyState
          icon="📦"
          title="No orders yet"
          description="Orders from customers will appear here."
        />
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <div
              key={order.id}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
            >
              <div className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-lg">
                        #{order.id.slice(0, 8).toUpperCase()}
                      </span>
                      <OrderStatusBadge status={order.status} />
                    </div>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="font-semibold text-gray-900">{order.customer_name}</p>
                        <p className="text-xs text-gray-400">{order.customer_phone}</p>
                        <p className="text-xs text-gray-400 mt-0.5">📍 {order.customer_address}</p>
                      </div>
                      <div>
                        <p className="font-bold text-blue-600">Rs {order.total?.toLocaleString()}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(order.created_at).toLocaleString("en-PK")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => fetchItems(order.id)}
                    className="flex items-center gap-1.5 text-xs border border-gray-200 px-3 py-2 rounded-xl font-medium text-gray-600 hover:bg-gray-50"
                  >
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${
                        expandedOrder === order.id ? "rotate-180" : ""
                      }`}
                    />
                    Items
                  </button>
                  <select
                    value={order.status}
                    onChange={(e) => updateStatus(order.id, e.target.value)}
                    className="text-xs border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                  >
                    {["pending", "confirmed", "preparing", "ready", "delivered", "cancelled"].map(
                      (s) => (
                        <option key={s} value={s}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              {expandedOrder === order.id && orderItems[order.id] && (
                <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-3">
                    Order Items
                  </p>
                  <div className="space-y-2">
                    {orderItems[order.id].map((item) => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="text-gray-700">
                          {item.menu_item_name}{" "}
                          <span className="text-gray-400">× {item.quantity}</span>
                        </span>
                        <span className="font-semibold">Rs {item.subtotal}</span>
                      </div>
                    ))}
                    {order.notes && (
                      <p className="text-xs text-gray-500 pt-2 border-t border-gray-200">
                        📝 {order.notes}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── RestaurantSettings ────────────────────────────────────────────────────────
const CITIES = ["Burewala", "Vehari"];
const CUISINE_OPTIONS = [
  "Biryani", "BBQ", "Karahi", "Desi", "Fast Food",
  "Sweets", "Drinks", "Burgers", "Pizza", "Seafood",
];

export function RestaurantSettings() {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [restaurantId, setRestaurantId] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("restaurants")
      .select("*")
      .eq("owner_id", user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setForm(data);
          setRestaurantId(data.id);
          setLogoPreview(data.logo_url);
          setCoverPreview(data.cover_url);
        }
        setLoading(false);
      });
  }, [user]);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    if (type === "logo") {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    } else {
      setCoverFile(file);
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  const toggleCuisine = (c) =>
    setForm((f) => ({
      ...f,
      cuisine_type: f.cuisine_type?.includes(c)
        ? f.cuisine_type.filter((x) => x !== c)
        : [...(f.cuisine_type || []), c],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let logo_url = form.logo_url;
      let cover_url = form.cover_url;
      if (logoFile)
        logo_url = await uploadImage(
          "restaurant-images",
          logoFile,
          `logos/${restaurantId}-logo`
        );
      if (coverFile)
        cover_url = await uploadImage(
          "restaurant-images",
          coverFile,
          `covers/${restaurantId}-cover`
        );
      const { error } = await supabase
        .from("restaurants")
        .update({ ...form, logo_url, cover_url })
        .eq("id", restaurantId);
      if (error) throw error;
      toast.success("Settings saved!");
    } catch (err) {
      toast.error(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  if (!form)
    return (
      <div className="text-center py-16 text-gray-400">
        No restaurant found. Contact admin.
      </div>
    );

  return (
    <div className="max-w-2xl space-y-6 page-enter">
      <h1 className="font-display text-2xl font-bold text-gray-900">
        Restaurant Settings
      </h1>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Images */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
          <h2 className="font-semibold text-gray-900">📸 Images</h2>
          <div className="grid grid-cols-2 gap-4">
            {[
              ["cover", coverPreview, setCoverPreview, setCoverFile],
              ["logo", logoPreview, setLogoPreview, setLogoFile],
            ].map(([type, preview, setPreview, setFile]) => (
              <div key={type}>
                <label className="text-xs font-semibold text-gray-500 uppercase block mb-2">
                  {type}
                </label>
                <div className="relative h-28 bg-gray-100 rounded-xl overflow-hidden border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors">
                  {preview ? (
                    <>
                      <img
                        src={preview}
                        alt={type}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPreview(null);
                          setFile(null);
                        }}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </>
                  ) : (
                    <label className="flex flex-col items-center justify-center h-full cursor-pointer text-gray-400 hover:text-blue-500">
                      <Upload className="w-5 h-5 mb-1" />
                      <span className="text-xs">Upload {type}</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileChange(e, type)}
                      />
                    </label>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
          <h2 className="font-semibold text-gray-900">📋 Basic Info</h2>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">
              Restaurant Name
            </label>
            <input
              value={form.name || ""}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="input-field"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">
              Description
            </label>
            <textarea
              value={form.description || ""}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              className="input-field resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">City</label>
              <select
                value={form.city || "Burewala"}
                onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                className="input-field"
              >
                {CITIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">
                Area / Location
              </label>
              <input
                value={form.location || ""}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                className="input-field"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">
              Full Address
            </label>
            <input
              value={form.address || ""}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              className="input-field"
            />
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
          <h2 className="font-semibold text-gray-900">📞 Contact</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Phone</label>
              <input
                value={form.contact || ""}
                onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))}
                className="input-field"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">WhatsApp</label>
              <input
                value={form.whatsapp || ""}
                onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))}
                className="input-field"
              />
            </div>
          </div>
        </div>

        {/* Hours & Cuisine */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
          <h2 className="font-semibold text-gray-900">⏰ Hours & Cuisine</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Opens At</label>
              <input
                type="time"
                value={form.opening_time || "09:00"}
                onChange={(e) => setForm((f) => ({ ...f, opening_time: e.target.value }))}
                className="input-field"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Closes At</label>
              <input
                type="time"
                value={form.closing_time || "23:00"}
                onChange={(e) => setForm((f) => ({ ...f, closing_time: e.target.value }))}
                className="input-field"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-2">
              Cuisine Types
            </label>
            <div className="flex flex-wrap gap-2">
              {CUISINE_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleCuisine(c)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    form.cuisine_type?.includes(c)
                      ? "bg-blue-600 text-white border-blue-600"
                      : "border-gray-200 text-gray-600"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div
              onClick={() => setForm((f) => ({ ...f, is_open: !f.is_open }))}
              className={`w-10 h-6 rounded-full transition-colors flex items-center px-1 cursor-pointer ${
                form.is_open ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <div
                className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  form.is_open ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </div>
            <span className="text-sm font-medium text-gray-700">Currently Open</span>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2"
        >
          {saving && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          )}
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </form>
    </div>
  );
}

export default RestaurantOrders;
